'use client';
import { useEffect, useRef, useState } from 'react';
import { useT } from '../../lib/i18n';
import type { ProductListItem } from '../../lib/site';
import { Icon } from '../Icon';
import { RelatedBooksModal } from './RelatedBooksModal';
import { freePlayerHref, openPlayer } from '../player/player-shared';

/**
 * 맛보기 · 관련교재 링크 — 카드는 `course-chips` 안, 리스트는 `clist-links` 안에 놓인다.
 *
 * **감싸개가 없다.** 카드형에는 원래 썸네일 위 반투명 띠(`course-links-wrap`)가 있었는데
 * 2026-08-26 가이드 개정으로 사라졌다 — 그림을 가리고 손가락으로 누르기 어려웠다.
 * 이제 차시·기간 배지와 한 줄에서 흐르고, 모양도 리스트와 같은 배지다.
 *
 * 드롭(`course-preview-drop`)은 **자리를 잡아 주는 상자의 직계 자식**이어야 한다 —
 * 가이드 CSS 가 그 상자를 기준으로 펼친다. 그래서 여기서 따로 감싸지 않는다.
 *
 * 맛보기는 현행 규칙대로 1개면 바로 재생, 여럿이면 드롭다운이다.
 */
export function CourseLinks({ p, freeVod, teacherName, iconOnly }: {
  p: ProductListItem;
  /** 맛보기 노출(설정 `listStyle.lecture.freevod`) */
  freeVod: boolean;
  /** 글자 없이 아이콘만 — **카드형**이 쓴다. 리스트는 자리가 있어 글자까지 둔다(가이드) */
  iconOnly?: boolean;
  teacherName?: string | null;
}) {
  const t = useT();
  const [open, setOpen] = useState(false);
  /** 내 링크 — 바깥 클릭 판정의 기준점(감싸개를 만들지 않으려고 링크에 단다) */
  const linkRef = useRef<HTMLAnchorElement>(null);
  const previews = freeVod ? p.previews : [];
  const hasBooks = p.relatedBookCount > 0;

  /*
   * 바깥을 누르면 닫는다 — 가이드 스크립트와 같은 판정(`.course-chips`·`.clist-links` 밖).
   * 감싸개를 하나 만들어 ref 를 달면 편하지만, 그 div 가 가이드에 없는 칸이 되어 칩 줄과
   * `.clist-links` 의 배치에 끼어든다. 그래서 **내 링크가 속한 상자**를 기준으로 본다.
   */
  useEffect(() => {
    if (!open) return;
    const box = linkRef.current?.closest('.course-chips, .clist-links') ?? null;
    const close = (e: MouseEvent) => {
      if (!box || !box.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('click', close);
    return () => document.removeEventListener('click', close);
  }, [open]);

  if (!previews.length && !hasBooks) return null;

  /**
   * 맛보기 재생(2026-09-17 사용자 지시 "강좌 리스트·상세에 맛보기 재생 되도록") — 재생 창은 **패널 없이** 연다.
   * `pid` 는 **그 차시를 맛보기로 걸어 둔 상품**이다(패키지면 구성 단과) — 서버가 그 짝으로 자격을 다시 본다.
   * 로그인 없이도 되는 자리다(현행 `free` 계승).
   */
  const play = (e: React.MouseEvent, v: { productId: number; curriculumId: number }) => {
    e.preventDefault();
    e.stopPropagation();
    setOpen(false);
    openPlayer(freePlayerHref(v.productId, v.curriculumId), { panel: false });
  };

  /*
   * 색은 **`badge-outline-primary`**(속이 빈 강조 테두리) — 카드·리스트 공통이다.
   * 글자는 **자리마다 갈린다** — 카드는 좁아서 아이콘만, 리스트는 글자까지 둔다.
   * (가이드가 `badge-dark` → `badge-primary` → `badge-outline-primary` 로 두 번 바뀌었다.
   *  색·문구는 디자이너가 정하는 것이라 가이드 HTML 을 그대로 따른다.)
   */
  const linkClass = 'badge badge-outline-primary course-link';

  // 뜻은 늘 `title` 이 함께 진다(가이드도 붙인다) — 아이콘만일 때는 그것이 유일한 설명이다
  const label = { title: t('prd.preview'), 'aria-label': t('prd.preview') };
  const text = iconOnly ? null : <> {t('prd.preview')}</>;
  const previewLink = previews.length === 1
    ? <a ref={linkRef} href="#" className={linkClass} {...label} onClick={(e) => play(e, previews[0])}><Icon name="play" />{text}</a>
    : previews.length > 1
      ? (
        <a ref={linkRef} href="#" className={linkClass} {...label}
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); setOpen(!open); }}>
          <Icon name="play" />{text}
        </a>
      )
      : null;

  /*
   * 맛보기가 여럿일 때 뜨는 **레이어** — 가이드가 `lecture-detail.html` 에서 바꾼 새 모양이다(2026-09-17 사용자 지시로 맞췄다).
   * 머리(제목 + 개수 배지) + 목록(번호·이름·재생) 두 층이고, 목록만 따로 스크롤한다(`preview-items`).
   * 전에는 옛 모양(`<a>` 한 줄에 아이콘과 글자)이었는데, 가이드 CSS 가 새 뼈대 기준으로 바뀌어 배치가 어긋났다.
   */
  const drop = previews.length > 1 && (
    <div className={`course-preview-drop${open ? ' open' : ''}`}>
      <div className="preview-panel-header">
        <Icon name="circle-play" />
        <span>{t('prd.previewPanel')}</span>
        <span className="preview-panel-cnt">{t('prd.previewCnt', { n: previews.length })}</span>
      </div>
      <div className="preview-items">
        {previews.map((v, i) => (
          <a key={v.curriculumId} href="#" className="preview-item" onClick={(e) => play(e, v)}>
            {/* 번호는 두 자리로 — 가이드가 `01`·`02` 로 맞춰 둔다(자리가 흔들리면 이름이 들쭉날쭉해진다) */}
            <span className="preview-item-num">{String(v.sort ?? i + 1).padStart(2, '0')}</span>
            <span className="preview-item-name">{v.name}</span>
            <span className="preview-item-play"><Icon name="play" /> {t('prd.play')}</span>
          </a>
        ))}
      </div>
    </div>
  );

  const books = hasBooks && (
    <RelatedBooksModal productId={p.id} count={p.relatedBookCount} teacherName={teacherName}
      className={linkClass} iconOnly={iconOnly} />
  );

  /*
   * **감싸개가 없다** — 카드는 `course-chips`, 리스트는 `clist-links` 의 직계 자식으로 놓는다
   * (가이드 그대로). 카드형이 쓰던 `course-links-wrap`(썸네일 위 반투명 띠)은 가이드에서
   * 사라졌다 — 그림을 가리고 손가락으로 누르기 어려웠다.
   */
  return <>{books}{previewLink}{drop}</>;
}
